import { z } from "zod";
import { getGoogleId } from "../../../../utils/Functions";
import { isMobilePhone } from "../../../auth/utils/AuthZodValidation";
import { useTranslation } from "react-i18next";

export function useEmployeeValidation(profilePhoneNumber) {
  const { t } = useTranslation();
  return z.object({
    username: z.string().min(1, { message: t("CantBeEmpty") }),
    firstName: z.string().min(1, { message: t("CantBeEmpty") }),
    lastName: z.string().min(1, { message: t("CantBeEmpty") }),
    address: z.string().optional(),
    city: z.string().optional(),
    country: z.string().nullish(),
    province: z.string().optional(),
    place: z.string().optional(),
    zipcode: z
      .string()
      .optional()
      .superRefine((data, ctx) => {
        return optionalNumberChecker(t, ctx, data, 5);
      }),
    phoneNumber: z
      .string()
      .min(1, { message: t("CantBeEmpty") })
      .refine(
        (data) => {
          if (data === profilePhoneNumber) return true;
          return isMobilePhone(data);
        },
        { message: t("InvalidPhoneNumber") },
      ),
    oib: z
      .string()
      .optional()
      .superRefine((data, ctx) => {
        return optionalNumberChecker(t, ctx, data, 13);
      }),

    idCardNumber: z
      .string()
      .optional()
      .superRefine((data, ctx) => {
        return optionalNumberChecker(t, ctx, data, 9);
      }),
    subscribeToEmails: z.boolean().optional(),
    dob: z.coerce.date().nullable().optional(),
  });
}

function useUserFormValidation(isBussines, profilePhoneNumber) {
  const { t } = useTranslation();
  if (isBussines) {
    return z.object({
      oib: z.string().superRefine((data, ctx) => {
        return optionalNumberChecker(t, ctx, data, 5);
      }),
      username: z.string().min(1, { message: t("CantBeEmpty") }),
      firstName: z.string().min(1, { message: t("CantBeEmpty") }),
      phoneNumber: z
        .string()
        .refine((value) => !value || isMobilePhone(value), {
          message: t("InvalidPhoneNumber"),
        }),
      email: optionalEmail(t),

      clinic: z.object({
        title: z.string().min(1, { message: t("CantBeEmpty") }),
        id: z.number().min(1, { message: t("CantBeEmpty") }),
      }),
      personToContact: z.string().min(1, { message: t("CantBeEmpty") }),
      address: z.string().min(1, { message: t("CantBeEmpty") }),
      country: z.string().min(1, { message: t("CantBeEmpty") }),
      place: z.string().min(1, { message: t("CantBeEmpty") }),
      city: z.string().min(1, { message: t("CantBeEmpty") }),
      province: z.string().min(1, { message: t("CantBeEmpty") }),
      zipcode: z.string().superRefine((data, ctx) => {
        return optionalNumberChecker(t, ctx, data, 5);
      }),
      shelter: z.boolean().optional(),
    });
  } else {
    return z.object({
      username: z.string().min(1, { message: t("CantBeEmpty") }),
      firstName: z.string().min(1, { message: t("CantBeEmpty") }),
      lastName: z.string().min(1, { message: t("CantBeEmpty") }),
      address: z.string().optional(),
      city: z.string().optional(),
      country: z.string().optional(),
      province: z.string().optional(),
      place: z.string().optional(),
      zipcode: z
        .string()
        .optional()
        .superRefine((data, ctx) => {
          return optionalNumberChecker(t, ctx, data, 5);
        }),
      phoneNumber: z
        .string()
        .min(1, { message: t("CantBeEmpty") })
        .refine(
          (data) => {
            if (data === profilePhoneNumber) return true;
            return isMobilePhone(data);
          },
          { message: t("InvalidPhoneNumber") },
        ),
      email: optionalEmail(t),
      oib: z
        .string()
        .optional()
        .superRefine((data, ctx) => {
          return optionalNumberChecker(t, ctx, data, 13);
        }),

      idCardNumber: z
        .string()
        .optional()
        .superRefine((data, ctx) => {
          return optionalNumberChecker(t, ctx, data, 9);
        }),
      subscribeToEmails: z.boolean().optional(),
      dob: z.coerce.date().nullable().optional(),
    });
  }
}

// Only rendered when editing an inactive user's email; absent/empty otherwise
function optionalEmail(t) {
  return z
    .string()
    .email({ message: t("InvalidEmail") })
    .optional()
    .or(z.literal(""));
}

export function optionalNumberChecker(t, ctx, data, minLen) {
  if (!data) return;
  return numberChecker(t, ctx, data, minLen);
}

export function numberChecker(t, ctx, data, minLen) {
  if (Number.isNaN(Number(data))) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("ValueMustBeANumber"),
    });
  }

  if (data.length < minLen && Number(data) < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("ValueIsTooShort"),
    });
  }
}

export { useUserFormValidation };
